"use client";

import React, { useEffect, useRef, useState } from "react";

interface PremiumIntroProps {
  onComplete?: () => void;
}

const introStyles = `
  /* --- Fullscreen Intro Overlay --- */
  #intro-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    background: linear-gradient(to right, #0D5735 3.3%, #008748 51.9%, #0D5735 99.5%);
    background-attachment: fixed;
    transition: opacity 0.8s ease-out;
  }

  #intro-overlay.fade-out {
    opacity: 0;
    pointer-events: none;
  }

  /* --- Vertical Brand Lockup Container --- */
  .brand-lockup {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    max-width: 90%;
  }

  /* --- Logo & Text Containers --- */
  #logo-container,
  #text-container {
    position: relative;
    opacity: 0;
    transform: scale(0.95);
    transition: opacity 1s ease-out, transform 1.8s cubic-bezier(0.25, 0.1, 0.25, 1);
  }

  #logo-container {
    width: 280px;
    height: auto;
  }

  #text-container {
    width: 320px;
    height: auto;
    margin-top: -30px;
  }

  #logo-container.reveal,
  #text-container.reveal {
    opacity: 1;
    transform: scale(1);
  }

  /* --- SVG Outlines & Fills --- */
  #intro-overlay svg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .border-draw {
    fill: #ffffff;
    fill-opacity: 0;
    stroke: #ffffff;
    stroke-width: 2px;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: fill-opacity 0.8s cubic-bezier(0.33, 1, 0.68, 1);
  }

  .border-draw.fill-active {
    fill-opacity: 1;
  }

  /* --- Mobile Responsiveness --- */
  @media (max-width: 768px) {
    #logo-container {
      width: 220px;
    }
    #text-container {
      width: 260px;
      margin-top: -20px;
    }
  }
`;

export default function PremiumIntro({ onComplete }: PremiumIntroProps) {
  const [fadeOut, setFadeOut] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const logoContainerRef = useRef<HTMLDivElement>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const logoSvgRef = useRef<SVGSVGElement>(null);
  const textSvgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    // Save original overflow state & lock scrolling during intro
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const wavePaths = logoSvgRef.current?.querySelectorAll<SVGPathElement>(".path-wave");
    const textPaths = textSvgRef.current?.querySelectorAll<SVGPathElement>(".path-text");

    const allWave = wavePaths ? Array.from(wavePaths) : [];
    const allText = textPaths ? Array.from(textPaths) : [];
    const allPaths = [...allWave, ...allText];

    // 1. Prepare ALL SVG Drawing Paths (Logo + Letters)
    allPaths.forEach((path) => {
      const length = path.getTotalLength() || 1500;
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = `${length}`;
      path.style.transition = "none";
    });

    // Force browser layout reflow
    if (logoContainerRef.current) {
      void logoContainerRef.current.offsetWidth;
    }

    // 2. Initial Soft Scaling Reveal of both containers
    const animFrame = requestAnimationFrame(() => {
      if (logoContainerRef.current) logoContainerRef.current.classList.add("reveal");
      if (textContainerRef.current) textContainerRef.current.classList.add("reveal");
    });

    const timers: NodeJS.Timeout[] = [];

    // 3. Moderate, Elegant Staggered Drawing Animation
    timers.push(
      setTimeout(() => {
        const drawTransition = "stroke-dashoffset 1.8s cubic-bezier(0.25, 0.8, 0.25, 1)";

        // Animate Logo Waves (Gentle Stagger)
        allWave.forEach((path, index) => {
          timers.push(
            setTimeout(() => {
              path.style.transition = drawTransition;
              path.style.strokeDashoffset = "0";
            }, index * 200)
          );
        });

        // Animate Text Letters (Slightly faster ripple effect)
        allText.forEach((path, index) => {
          timers.push(
            setTimeout(() => {
              path.style.transition = drawTransition;
              path.style.strokeDashoffset = "0";
            }, 350 + index * 80)
          );
        });
      }, 250)
    );

    // 4. Fill active phase (smoothly fade fill-opacity to 1)
    timers.push(
      setTimeout(() => {
        allPaths.forEach((path) => {
          path.style.transition = "fill-opacity 0.8s cubic-bezier(0.33, 1, 0.68, 1)";
          path.classList.add("fill-active");
        });
      }, 1900)
    );

    // 5. Fade out the whole overlay shortly after fill begins/completes
    timers.push(
      setTimeout(() => {
        setFadeOut(true);
      }, 2600)
    );

    // 6. Complete animation & restore scroll
    timers.push(
      setTimeout(() => {
        document.body.style.overflow = originalOverflow;
        setIsCompleted(true);
        if (onComplete) {
          onComplete();
        }
      }, 3400)
    );

    return () => {
      cancelAnimationFrame(animFrame);
      timers.forEach((t) => clearTimeout(t));
      document.body.style.overflow = originalOverflow;
    };
  }, [onComplete]);

  if (isCompleted) return null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: introStyles }} />

      <div
        id="intro-overlay"
        className={fadeOut ? "fade-out" : ""}
      >
        {/* Unified vertical brand lockup */}
        <div className="brand-lockup" id="brand-lockup">
          {/* Wave Logo Icon */}
          <div id="logo-container" ref={logoContainerRef}>
            <svg
              ref={logoSvgRef}
              id="intro-logo"
              viewBox="0 0 721 327"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Left Circular Swirl */}
              <path
                className="border-draw path-wave"
                d="M231.432 73.3901C175.852 22.8601 105.582 39.4601 61.5519 64.0101C17.5219 88.5501 -5.57811 140.77 1.15189 178.31C7.89189 215.85 37.2419 249.05 86.0919 250.98C134.942 252.9 176.812 224.03 177.532 165.8C178.252 107.57 129.072 86.1501 129.072 86.1501C165.312 124.89 151.542 150.16 133.982 171.33C116.412 192.5 59.1519 207.18 33.4019 166.04C7.65189 124.9 29.7919 85.6701 63.7219 72.6801C97.6519 59.6901 130.612 59.6901 161.892 72.6801C193.172 85.6701 252.362 137.65 231.672 220.42C210.982 303.19 121.462 324.78 121.462 324.78C237.202 322.06 265.832 250.26 272.572 225.24C279.322 200.2 287.022 123.92 231.432 73.3901ZM25.7019 188.57C27.1519 190.88 28.9119 192.94 30.5619 195.08C34.1019 199.15 38.0419 202.84 42.4019 205.95C51.1819 212.06 61.4819 215.86 72.2419 217.15C77.6019 217.88 83.0619 218.12 88.5319 217.98L92.6419 217.79C94.0019 217.69 95.3119 217.61 96.6519 217.42C99.3219 217.11 101.962 216.58 104.602 215.98C109.872 214.77 115.022 212.97 119.952 210.7C122.402 209.55 124.812 208.29 127.132 206.89L128.882 205.85L130.582 204.73C131.732 204.01 132.802 203.17 133.902 202.38C138.252 199.13 142.142 195.29 145.582 191.08C146.482 190.06 147.222 188.91 148.052 187.83C148.842 186.72 149.552 185.56 150.312 184.42C150.972 183.23 151.692 182.06 152.312 180.85C152.892 179.62 153.572 178.42 154.082 177.15C158.602 167.11 160.302 155.87 160.222 144.63C161.722 155.78 160.922 167.45 157.022 178.33C153.152 189.22 146.002 198.94 137.022 206.33C132.532 210.03 127.622 213.23 122.392 215.8C117.172 218.38 111.712 220.46 106.052 221.92C103.232 222.67 100.352 223.23 97.4319 223.61C95.9819 223.83 94.4919 223.94 93.0519 224.06L88.7319 224.3C82.9619 224.49 77.1519 224.31 71.3719 223.56C59.8219 222.05 48.3919 217.86 38.8319 210.91C34.1119 207.36 29.9219 203.2 26.2019 198.68C24.4819 196.32 22.6319 194.05 21.1819 191.53C19.6419 189.09 18.1419 186.53 16.9819 183.82C16.3419 182.49 15.8719 181.1 15.3219 179.74L14.6319 177.66C14.3919 176.97 14.1819 176.27 14.0019 175.57C13.1619 172.77 12.6319 169.92 12.1819 167.06C11.3719 161.33 11.1619 155.57 11.4619 149.87C11.7919 144.17 12.5519 138.52 13.7319 132.98C14.9219 127.44 16.4319 121.97 18.4919 116.73C17.7919 119.46 17.2119 122.21 16.7319 124.97C16.2319 127.73 15.8119 130.5 15.4919 133.27C14.8519 138.82 14.4919 144.39 14.6219 149.93C14.7819 160.98 16.7419 172.05 21.5419 181.68C22.6819 184.08 24.1419 186.31 25.7019 188.57Z"
              />
              {/* Middle Curve */}
              <path
                className="border-draw path-wave"
                d="M183.552 324.77C183.552 324.77 282.752 308.96 447.272 0.00012207H546.172C546.172 0.00012207 493.472 96.7301 427.782 182.63C362.092 268.53 335.622 336.24 183.552 324.77Z"
              />
              {/* Right Sharp Wing */}
              <path
                className="border-draw path-wave"
                d="M316.372 324.36C316.372 324.36 409.812 297.89 575.362 0.00012207H720.142L665.282 116.94H618.602C618.602 116.94 571.442 206.45 523.792 255.54C476.142 304.63 420.322 328.21 316.372 324.36Z"
              />
            </svg>
          </div>

          {/* SVG Text: "VIET THIEN" */}
          <div id="text-container" ref={textContainerRef}>
            <svg
              ref={textSvgRef}
              id="intro-text"
              viewBox="0 0 868 201"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="border-draw path-text"
                d="M48.12 174.11L72.14 94.4H96.45L59.54 200.39H36.75L0 94.4H24.24L48.12 174.11Z"
              />
              <path
                className="border-draw path-text"
                d="M129.65 200.39H107.81V94.4H129.65V200.39Z"
              />
              <path
                className="border-draw path-text"
                d="M213.66 154.46H171.73V182.85H220.94V200.39H149.89V94.4H220.8V112.09H171.73V137.35H213.66V154.46Z"
              />
              <path
                className="border-draw path-text"
                d="M315 112.09H282.53V200.39H260.69V112.09H228.66V94.4H315V112.09Z"
              />
              <path
                className="border-draw path-text"
                d="M435.62 112.09H403.15V200.39H381.31V112.09H349.28V94.4H435.62V112.09Z"
              />
              <path
                className="border-draw path-text"
                d="M534.34 200.39H512.5V154.96H469.91V200.39H448.07V94.4H469.91V137.35H512.5V94.4H534.34V200.39Z"
              />
              <path
                className="border-draw path-text"
                d="M576.64 200.39H554.8V94.4H576.64V200.39Z"
              />
              <path
                className="border-draw path-text"
                d="M660.64 154.46H618.71V182.85H667.92V200.39H596.87V94.4H667.78V112.09H618.71V137.35H660.64V154.46Z"
              />
              <path
                className="border-draw path-text"
                d="M766.93 200.39H745.09L702.58 130.65V200.39H680.74V94.4H702.58L745.17 164.29V94.4H766.94V200.39H766.93Z"
              />
              <path
                className="border-draw path-text"
                d="M823.39 87.9C799.15 87.9 779.44 68.18 779.44 43.95C779.44 19.72 799.16 0 823.39 0C847.62 0 867.34 19.72 867.34 43.95C867.34 68.18 847.62 87.9 823.39 87.9ZM823.39 7.15997C803.11 7.15997 786.6 23.66 786.6 43.95C786.6 64.23 803.1 80.74 823.39 80.74C843.67 80.74 860.18 64.24 860.18 43.95C860.18 23.67 843.67 7.15997 823.39 7.15997Z"
              />
              <path
                className="border-draw path-text"
                d="M824.16 50.11H815.51V67.29H807.75V22.62H823.46C828.62 22.62 832.59 23.78 835.4 26.09C838.2 28.4 839.6 31.75 839.6 36.13C839.6 39.12 838.88 41.62 837.44 43.64C836 45.66 833.99 47.21 831.41 48.29L841.44 66.89V67.29H833.13L824.16 50.11ZM815.51 43.85H823.49C826.11 43.85 828.15 43.19 829.63 41.87C831.1 40.55 831.84 38.75 831.84 36.45C831.84 34.06 831.16 29.59 829.8 30.9C828.44 29.59 826.41 28.92 823.71 28.88H815.52V43.85H815.51Z"
              />
            </svg>
          </div>
        </div>
      </div>
    </>
  );
}

